import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { OverlayPanelModule } from 'primeng/overlaypanel';

import { NetsapiensDomain } from '@voice/utils';
import { DomainsService } from '../../../services/domains.service';
import { NotificationsService } from '../../../services/notifications.service';
import { FaxAuditService } from '../../../services/fax-audit.service';
import { FaxBackAccount, FaxBackResponse } from '../../../models/faxback.model';
import { DomainMultipleSelectorComponent } from '../../domain-multiple-selector/domain-multiple-selector.component';
import { DomainTableData } from '../shared/types/domain-data.types';
import {
  FAX_AUDIT_FILTER_FIELDS,
  FAX_AUDIT_COLUMNS,
  FAX_AUDIT_DEFAULT_PAGE_SIZE,
  FAX_AUDIT_PAGE_SIZE_OPTIONS,
  FAX_AUDIT_MESSAGES,
} from './constants/fax-audit.constants';
import { FaxAuditTableService } from './services/fax-audit-table.service';
import { FaxAuditMapperService } from './services/fax-audit-mapper.service';
import { FaxAuditUpdateService } from './services/fax-audit-update.service';
import { FilterFieldOption, ColumnConfig } from '../shared/types/lookup-screen.types';
import { FaxAuditTourService } from './fax-audit-tour.service';

@Component({
  selector: 'app-fax-audit',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    CheckboxModule,
    FormsModule,
    ButtonModule,
    TooltipModule,
    InputTextModule,
    MultiSelectModule,
    OverlayPanelModule,
    DomainMultipleSelectorComponent,
  ],
  templateUrl: './fax-audit.component.html',
  styleUrl: './fax-audit.component.scss',
})
export class FaxAuditComponent implements OnInit {
  private readonly domainsService = inject(DomainsService);
  private readonly faxAuditService = inject(FaxAuditService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly tableService = inject(FaxAuditTableService);
  private readonly mapperService = inject(FaxAuditMapperService);
  private readonly updateService = inject(FaxAuditUpdateService);
  private readonly tourService = inject(FaxAuditTourService);

  domains: NetsapiensDomain[] = [];
  tableData: DomainTableData<FaxBackAccount>[] = [];
  requesting = false;
  collapsed = false;

  readonly availableFilterFields: FilterFieldOption[] = FAX_AUDIT_FILTER_FIELDS;
  readonly columns: ColumnConfig[] = FAX_AUDIT_COLUMNS;
  readonly defaultPageSize = FAX_AUDIT_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = FAX_AUDIT_PAGE_SIZE_OPTIONS;
  readonly messages = FAX_AUDIT_MESSAGES;

  startTour(): void {
    this.tourService.start();
  }

  ngOnInit(): void {
    this.loadDomains();
  }

  onDomainSelectionChanged(domains: string[] | null): void {
    if (domains) {
      this.loadFaxAccounts(domains);
    } else {
      this.resetData();
    }
  }

  onSearch(tableData: DomainTableData<FaxBackAccount>): void {
    this.tableService.applyFilters(tableData);
  }

  onClearSearch(tableData: DomainTableData<FaxBackAccount>): void {
    this.tableService.clearSearch(tableData);
  }

  onFilterChange(tableData: DomainTableData<FaxBackAccount>): void {
    this.tableService.applyFilters(tableData);
  }

  onUpdateAccounts(accounts: FaxBackAccount[]): void {
    if (!accounts.length) {
      return;
    }

    this.updateService.updateAccounts(accounts).subscribe({
      next: (result) => {
        this.notificationsService.success(result.message);
      },
      error: (error) => {
        this.notificationsService.error(error);
      },
    });
  }

  isColumnVisible(
    tableData: DomainTableData<FaxBackAccount>,
    column: string,
  ): boolean {
    return this.tableService.isColumnVisible(tableData, column);
  }

  getVisibleColumnsCount(
    tableData: DomainTableData<FaxBackAccount>,
  ): number {
    return this.tableService.getVisibleColumnsCount(tableData);
  }

  hasSelectedAccounts(tableData: DomainTableData<FaxBackAccount>): boolean {
    return (
      tableData.selectedItems !== undefined &&
      tableData.selectedItems.length > 0
    );
  }

  getDomainSummary(
    tableData: DomainTableData<FaxBackAccount>,
  ): { domainId: string; accountCount: number } {
    return {
      domainId: tableData.domainId,
      accountCount: tableData.items.length,
    };
  }

  private loadDomains(): void {
    this.domainsService.getDomains().subscribe({
      next: (domains) => {
        this.domains = domains;
      },
      error: (error) => {
        this.notificationsService.error(error);
      },
    });
  }

  private loadFaxAccounts(domains: string[]): void {
    this.requesting = true;
    this.faxAuditService.getAccounts(domains).subscribe({
      next: (response: FaxBackResponse) => {
        this.tableData = this.mapperService.mapResponseToTableData(response);
        this.requesting = false;
        this.collapsed = true;
      },
      error: (error) => {
        this.requesting = false;
        this.notificationsService.error(error);
      },
    });
  }

  private resetData(): void {
    this.tableData = [];
    this.collapsed = false;
  }
}
